import React, { createContext, useContext, useState, useEffect } from 'react';
import { getMe } from './api';

const AuthContext = createContext(null);

const TRIAL_DAYS = 7;
const FREE_SCRIPTS = 7;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scriptCount, setScriptCount] = useState(0);

  const deriveScriptCount = (userData) => {
    // Use server's free_scripts_remaining to calculate scripts used — avoids stale localStorage
    const remaining = userData?.free_scripts_remaining ?? FREE_SCRIPTS;
    return Math.max(0, FREE_SCRIPTS - remaining);
  };

  useEffect(() => {
    const token = localStorage.getItem('mts_token');
    if (token) {
      getMe()
        .then(r => {
          setUser(r.data);
          const count = deriveScriptCount(r.data);
          setScriptCount(count);
          if (!localStorage.getItem('mts_trial_start')) {
            localStorage.setItem('mts_trial_start', new Date().toISOString());
          }
        })
        .catch(() => localStorage.removeItem('mts_token'))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('mts_token', token);
    const isNewUser = !localStorage.getItem('mts_trial_start');
    if (isNewUser) {
      localStorage.setItem('mts_trial_start', new Date().toISOString());
      window.gtag?.('event', 'sign_up', { method: 'email' });
    }
    // Clear any previous user's progress data so it doesn't bleed into new sessions
    localStorage.removeItem('mts_progress');
    const count = deriveScriptCount(userData);
    setScriptCount(count);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('mts_token');
    localStorage.removeItem('mts_script_count');
    localStorage.removeItem('mts_trial_start');
    localStorage.removeItem('mts_progress');
    setUser(null);
    setScriptCount(0);
  };

  const incrementScriptCount = () => {
    const newCount = scriptCount + 1;
    setScriptCount(newCount);
    localStorage.setItem('mts_script_count', newCount.toString());
  };

  const getTrialDaysRemaining = () => {
    const trialStart = localStorage.getItem('mts_trial_start');
    if (!trialStart) return TRIAL_DAYS;
    const start = new Date(trialStart);
    const now = new Date();
    const diffMs = now - start;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, TRIAL_DAYS - diffDays);
  };

  const isSubscribed = user?.subscription_status === 'active';
  const isCancelled = user?.subscription_status === 'cancelled';
  const isTrial = user?.subscription_status === 'trial';
  const trialDaysRemaining = getTrialDaysRemaining();
  // Pro users get unlimited — don't count their scripts against the free limit
  const scriptsRemaining = isSubscribed ? 999 : user ? Math.max(0, FREE_SCRIPTS - scriptCount) : FREE_SCRIPTS;
  const trialExpired = isSubscribed ? false : trialDaysRemaining === 0;
  const canCreateScript = isSubscribed || (scriptsRemaining > 0 && !trialExpired && !isCancelled);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout,
      scriptCount, incrementScriptCount,
      isSubscribed, isTrial, isCancelled,
      scriptsRemaining, canCreateScript,
      trialDaysRemaining, trialExpired,
      FREE_SCRIPTS, TRIAL_DAYS
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
